import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Plus, Trash2, Video, Image, Smartphone, Layers } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { PageHeader } from '@/components/PageHeader';
import { EmptyState } from '@/components/EmptyState';
import { getCreatives, createCreative, deleteCreative, getClients } from '@/lib/api';
import { toast } from 'sonner';

const TYPES = [
  { value: 'ugc_video', label: 'UGC Video', icon: Video },
  { value: 'static_banner', label: 'Static Banner', icon: Image },
  { value: 'playable', label: 'Playable Ad', icon: Smartphone },
  { value: 'carousel', label: 'Carousel', icon: Layers },
];

const fadeIn = { hidden: { opacity: 0, y: 12 }, visible: { opacity: 1, y: 0, transition: { duration: 0.3 } } };

export default function AdminCreativesPage() {
  const [creatives, setCreatives] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ client_id: '', name: '', type: 'ugc_video', channel: 'meta', status: 'active', description: '' });

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [crRes, clRes] = await Promise.all([getCreatives(), getClients()]);
      setCreatives(crRes.data);
      setClients(clRes.data);
    } catch (err) { console.error(err); } finally { setLoading(false); }
  };

  const handleSave = async () => {
    try {
      await createCreative(form);
      toast.success('Creative erstellt');
      setDialogOpen(false);
      loadData();
    } catch (err) { toast.error('Fehler'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Creative löschen?')) return;
    try { await deleteCreative(id); toast.success('Gelöscht'); loadData(); } catch (err) { toast.error('Fehler'); }
  };

  const getClientName = (id) => clients.find(c => c.id === id)?.name || 'Unbekannt';
  const getTypeInfo = (type) => TYPES.find(t => t.value === type) || TYPES[0];

  return (
    <motion.div initial="hidden" animate="visible" variants={{ visible: { transition: { staggerChildren: 0.05 } } }}>
      <PageHeader title="Creatives" description={`${creatives.length} Creative Assets`}>
        <Button onClick={() => { setForm({ client_id: clients[0]?.id || '', name: '', type: 'ugc_video', channel: 'meta', status: 'active', description: '' }); setDialogOpen(true); }} className="btn-lift bg-slate-900 text-white hover:bg-slate-800" data-testid="creative-create-button">
          <Plus className="w-4 h-4 mr-2" /> Neues Creative
        </Button>
      </PageHeader>

      {creatives.length === 0 && !loading ? (
        <EmptyState title="Keine Creatives" description="Erstellen Sie Ihr erstes Creative Asset." actionLabel="Creative erstellen" action={() => setDialogOpen(true)} />
      ) : (
        <motion.div variants={fadeIn} className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {creatives.map((cr, i) => {
            const typeInfo = getTypeInfo(cr.type);
            const TypeIcon = typeInfo.icon;
            return (
              <div key={cr.id || i} className="kpi-card-hover rounded-xl border border-slate-200 bg-white p-5">
                <div className="flex items-start justify-between mb-3">
                  <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                    <TypeIcon className="w-5 h-5 text-slate-600" />
                  </div>
                  <div className="flex gap-1">
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${cr.status === 'active' ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>
                      {cr.status === 'active' ? 'Aktiv' : cr.status === 'paused' ? 'Pausiert' : 'Archiviert'}
                    </span>
                    <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-400" onClick={() => handleDelete(cr.id)}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
                <h3 className="text-sm font-semibold text-slate-900 mb-1">{cr.name}</h3>
                <p className="text-xs text-slate-500 mb-2">{getClientName(cr.client_id)} | {typeInfo.label}</p>
                {cr.description && <p className="text-xs text-slate-400 mb-3">{cr.description}</p>}
                {/* Performance Metrics */}
                {(cr.ctr || cr.hook_rate || cr.performance_score) && (
                  <div className="pt-3 border-t border-slate-100 grid grid-cols-3 gap-2">
                    {cr.ctr != null && (
                      <div className="text-center">
                        <p className="text-[10px] text-slate-400">CTR</p>
                        <p className="font-display text-sm font-bold text-slate-900">{cr.ctr}%</p>
                      </div>
                    )}
                    {cr.hook_rate != null && (
                      <div className="text-center">
                        <p className="text-[10px] text-slate-400">Hook-Rate</p>
                        <p className="font-display text-sm font-bold text-slate-900">{cr.hook_rate}%</p>
                      </div>
                    )}
                    {cr.performance_score != null && (
                      <div className="text-center">
                        <p className="text-[10px] text-slate-400">Score</p>
                        <p className={`font-display text-sm font-bold ${cr.performance_score >= 70 ? 'text-emerald-600' : cr.performance_score >= 40 ? 'text-amber-600' : 'text-red-600'}`}>{cr.performance_score}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </motion.div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display">Neues Creative</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div><label className="text-sm font-medium text-slate-700 mb-1.5 block">Name *</label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="Creative Name" data-testid="creative-form-name" /></div>
            <div><label className="text-sm font-medium text-slate-700 mb-1.5 block">Kunde</label>
              <Select value={form.client_id} onValueChange={v => setForm({...form, client_id: v})}><SelectTrigger><SelectValue placeholder="Kunde" /></SelectTrigger><SelectContent>{clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent></Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-sm font-medium text-slate-700 mb-1.5 block">Typ</label>
                <Select value={form.type} onValueChange={v => setForm({...form, type: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent></Select>
              </div>
              <div><label className="text-sm font-medium text-slate-700 mb-1.5 block">Status</label>
                <Select value={form.status} onValueChange={v => setForm({...form, status: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="active">Aktiv</SelectItem><SelectItem value="paused">Pausiert</SelectItem><SelectItem value="archived">Archiviert</SelectItem></SelectContent></Select>
              </div>
            </div>
            <div><label className="text-sm font-medium text-slate-700 mb-1.5 block">Beschreibung</label><Textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} placeholder="Beschreibung..." /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Abbrechen</Button>
            <Button onClick={handleSave} className="btn-lift bg-slate-900 text-white hover:bg-slate-800" data-testid="creative-form-save">Erstellen</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
}
